/**
 * トレーニング経過(2026-10-08 発注者指示。D-197)の計算。画面では計算しない。
 *
 * 単位は「人 × カテゴリ(給湯器/不用品)」= トラック。現行の採用データ(シート)と同じく、
 * 給湯器と不用品は別々に 15h/30h/60h/90h を数える(シートは「齋藤彰宏」と「齋藤彰宏W」が別の行)。
 *
 *  - 時間 = 実稼働(daily_summary.staff[].worked_hours。在宅=受付+通話+後処理、出勤=拘束−休憩−離席)
 *  - 取得 = daily_summary.staff[].appointments
 *  - 5/1 以降に稼働を始めた人は 稼働日から数える(2026-10-09 に 6/1 から 5/1 へ変更)。7/31 までは採用データの日別の数字、8/1 からは日次集計
 *  - 5/1 より前から在籍の人・採用データに無い人は 9/1 から数える(一覧に同じように出す。面談のアラートは出さない)
 *  - 段階の判定は1日単位: その日の終わりに累計が段階に届いたら、その日を「到達日」とし、
 *    前の段階からその日までを「区間」とする(採用データの 15h/30h/60h/90h の数字と同じ求め方。2026-10-08 照合で一致)
 *  - 面談・研修の効果 = 面談日の前の15h と 後の15h の 1時間あたり取得の差(面談日は含めない。1日単位なので15hちょうどにはならない)
 */
import type { BusinessType, DailySummary, DateStr, Operator } from '@core/lib/types';

/** 段階(累計の実稼働)。90h の後は 30h ごとに続く(120h・150h…。2026-10-14 発注者指示) */
export const MILESTONES = [15, 30, 60, 90] as const;
/** 90h より後の段階の間隔 */
export const MILESTONE_STEP = 30;
/** i 番目(0始まり)の段階 */
export function milestoneAt(i: number): number {
  return i < MILESTONES.length ? MILESTONES[i]! : MILESTONES[MILESTONES.length - 1]! + MILESTONE_STEP * (i - MILESTONES.length + 1);
}
/**
 * 一覧に出す段階の列。15h〜90h と、それより後は「だれかが届いた段階」まで(無限には増やさない)
 */
export function displayMilestones(tracks: Pick<Track, 'milestones'>[]): number[] {
  const reached = Math.max(0, ...tracks.flatMap((t) => t.milestones.map((m) => m.milestone)));
  const out: number[] = [];
  for (let i = 0; ; i++) {
    const m = milestoneAt(i);
    if (i >= MILESTONES.length && m > reached) break;
    out.push(m);
  }
  return out;
}
/**
 * 目標の1時間あたり取得(2026-10-14 発注者指示: 30h で 1.5件/h・60h で 2.0件/h 以上)。区間・累計の両方に色を付ける。
 * 給湯器だけ(不用品は目標なし。2026-10-14 発注者回答)
 */
export const RATE_TARGETS: Partial<Record<number, number>> = { 30: 1.5, 60: 2.0 };
export function rateTarget(business: BusinessType, milestone: number): number | null {
  return business === 'kyutoki' ? (RATE_TARGETS[milestone] ?? null) : null;
}
/** 面談のアラートを出す段階(90h は成績の記録だけ) */
export const ALERT_MILESTONES = [15, 30, 60] as const;
/** この日より前は採用データ(シート)の日別の数字を使う。この日からは日次集計 */
export const SHEET_UNTIL: DateStr = '2026-08-01';
/** 稼働開始がこの日以降なら、稼働日から数える(新人) */
export const NEW_HIRE_FROM: DateStr = '2026-05-01';
/** 既存スタッフ(5/1 より前から在籍・採用データに無い人)を数え始める日 */
export const VETERAN_COUNT_FROM: DateStr = '2026-09-01';
/** 採用データにも稼働開始日の指定にも無い人で、日次集計の最初の稼働がこの日以降なら新人とみなす */
export const AUTO_NEW_HIRE_FROM: DateStr = '2026-10-01';
/** 面談のアラートは、この日以降に届いた段階だけ出す(それより前の段階は成績の記録だけ) */
export const ALERT_FROM: DateStr = '2026-10-01';
/** 面談の前後で比べる時間 */
export const EFFECT_HOURS = 15;

export type Milestone = number;

/** 採用データの日別の数字。[取得, 時間] */
export type SheetDays = Record<DateStr, [number, number]>;

/** training_tracks/{op_id}_{business} */
export type TrainingTrackDoc = {
  id: string;
  op_id: string;
  business: BusinessType;
  /** 稼働開始日(採用データの「稼働日」か、管理者の指定)。null = 指定なし */
  start_date: DateStr | null;
  start_source: 'sheet' | 'manual' | null;
  /** 採用データの「稼働日」(管理者の指定を外したときに戻す値) */
  sheet_start_date?: DateStr | null;
  /** 採用データの名前(取込のときの照合の確認用) */
  sheet_name?: string | null;
  /** 採用データの日別の数字(SHEET_UNTIL より前の日だけ) */
  sheet_days?: SheetDays;
  /** 面談予定日(管理者が詳細で入れる。2026-10-15 発注者指示)。null = 未定 */
  interview_plan_date?: DateStr | null;
};

/** training_records(面談・研修の記録) */
export type TrainingRecordKind = 'interview' | 'training' | 'coaching';
/** 種類。「指導」は 2026-10-20 発注者指示で追加(面談の段階の記録には数えない。面談前後15hの効果にも入れない=同日 発注者回答) */
export const RECORD_KIND_LABELS: Record<TrainingRecordKind, string> = { interview: '面談', training: '研修', coaching: '指導' };
/** 面談前後15hの効果に数える種類(指導は数えない) */
export const countsForEffect = (kind: TrainingRecordKind): boolean => kind !== 'coaching';
/** 面談で選ぶ課題(給湯器。2026-10-15 発注者指示。過去の面談結果から洗い出した11個) */
export const ISSUE_TAGS = [
  '冒頭トーク',
  '断られた後の切り返し',
  '会話の主導権・回答後に本題へ戻す',
  'Q&Aの理解',
  '日程提案・クロージング',
  '声・話し方(大きさ・笑声・スピード)',
  '絡まれた時の対応',
  'スクリプト・社内ルールの順守',
  '処理・システム操作',
  '離席・稼働時間の確保',
  '振り返り・相談の習慣',
] as const;
/** やったことのタグ(過去の面談結果から。当てはまらないときは「その他」) */
export const ACTION_TAGS = ['数字の振り返り', 'ロールプレイ(RP)', 'トーク指導', '通話ログの確認', '操作の指導', 'その他'] as const;
export type TrainingAction = { tag: string; memo: string };

export type TrainingRecord = {
  id: string;
  op_id: string;
  business: BusinessType;
  date: DateStr;
  kind: TrainingRecordKind;
  /** どの段階の面談か(段階と関係ない面談・研修は null) */
  milestone: Milestone | null;
  /** 担当者 */
  trainer: string;
  /** 備考(自由に書く欄。2026-10-15 までの呼び名はレポート) */
  report: string;
  /** 課題(複数選択。給湯器だけ。2026-10-15 発注者指示)。古い記録には無い */
  issues?: string[];
  /** やったこと(タグ＋詳細のメモを人ごとに足していく。2026-10-15 発注者指示)。古い記録には無い */
  actions?: TrainingAction[];
  created_by?: string;
  created_at?: unknown;
  updated_by?: string;
  updated_at?: unknown;
};

/** training_milestones/{op_id}_{business}_{h}(到達したときの成績の控え) */
export type SavedMilestone = {
  id: string;
  op_id: string;
  business: BusinessType;
  milestone: Milestone;
  reached_date: DateStr;
  seg_appointments: number;
  seg_hours: number;
  cum_appointments: number;
  cum_hours: number;
  recorded_by?: string;
  recorded_at?: unknown;
};

export type TrackDay = {
  date: DateStr;
  appointments: number;
  hours: number;
  /** 会社別(日次集計の割振)。採用データの日は会社が分からないので空 */
  byCompany: Record<string, { appointments: number; hours: number }>;
  source: 'sheet' | 'system';
  /** 通話回数・通話時間・後処理時間(秒)・後処理回数(日次集計の日だけ。採用データの日は無い) */
  calls?: number;
  talkSec?: number;
  afterSec?: number;
  afterCount?: number;
};

export type MilestoneResult = {
  milestone: Milestone;
  reachedDate: DateStr;
  segAppointments: number;
  segHours: number;
  /** 区間の1時間あたり取得(時間0なら null) */
  segRate: number | null;
  /** 前の段階の区間との差(15h は null) */
  diff: number | null;
  cumAppointments: number;
  cumHours: number;
  /** 稼働開始からその段階までの累計の1時間あたり取得(2026-10-14) */
  cumRate: number | null;
  /** 前の段階の累計との差(15h は null。2026-10-14 発注者指示「累計も差異の追加」) */
  cumDiff: number | null;
};

export type Track = {
  key: string;
  opId: string;
  opCode: string;
  name: string;
  business: BusinessType;
  /** 新人(稼働日から数える・面談のアラートの対象) */
  isNew: boolean;
  /** 稼働開始日(新人のとき) */
  startDate: DateStr | null;
  startSource: 'sheet' | 'manual' | 'auto' | null;
  /** 数え始めた日 */
  countFrom: DateStr;
  days: TrackDay[];
  /** 最後に稼働した日(数え始めより前も含む。2026-10-14) */
  lastWorkDate: DateStr | null;
  totalAppointments: number;
  totalHours: number;
  /** 数え始めからの 通話時間Av・後処理時間Av・1時間あたりの通話回数(日次集計の日だけ。2026-10-20 発注者指示) */
  calls: CallStats;
  milestones: MilestoneResult[];
  /** 次の段階(全部届いたら null) */
  next: { milestone: Milestone; segAppointments: number; segHours: number; remaining: number } | null;
};

/** 通話の数字。採用データの日は通話の記録が無いので入れない(面談予定日の30日間と同じ数え方) */
export type CallStats = { talkAvgSec: number | null; afterAvgSec: number | null; callsPerHour: number | null };

/** 通話時間Av = 通話時間 ÷ 通話回数、後処理時間Av = 後処理時間 ÷ 後処理回数、1時間あたりの通話回数 = 通話回数 ÷ 実稼働 */
export function callStatsOf(days: TrackDay[]): CallStats {
  let calls = 0;
  let talkSec = 0;
  let afterSec = 0;
  let afterCount = 0;
  let hours = 0;
  for (const d of days) {
    if (d.source !== 'system') continue;
    hours += d.hours;
    calls += d.calls ?? 0;
    talkSec += d.talkSec ?? 0;
    afterSec += d.afterSec ?? 0;
    afterCount += d.afterCount ?? 0;
  }
  return {
    talkAvgSec: calls > 0 ? talkSec / calls : null,
    afterAvgSec: afterCount > 0 ? afterSec / afterCount : null,
    callsPerHour: hours > 0 ? calls / hours : null,
  };
}

/** 1か月の成績(2026-10-20 発注者指示「月別の成績と平均」)。月は 'YYYY-MM' */
export type MonthStat = { month: string; hours: number; appointments: number; rate: number | null; calls: CallStats };

/** その人(カテゴリ)の月別の成績。稼働も取得も無い月は出さない(新しい月が先) */
export function monthlyStats(days: TrackDay[]): MonthStat[] {
  const byMonth = new Map<string, TrackDay[]>();
  for (const d of days) {
    if (d.hours <= 0 && d.appointments <= 0) continue;
    const m = d.date.slice(0, 7);
    byMonth.set(m, [...(byMonth.get(m) ?? []), d]);
  }
  return [...byMonth.entries()]
    .map(([month, list]) => {
      const hours = round2(list.reduce((a, d) => a + d.hours, 0));
      const appointments = list.reduce((a, d) => a + d.appointments, 0);
      return { month, hours, appointments, rate: rateOf(appointments, hours), calls: callStatsOf(list) };
    })
    .sort((a, b) => (a.month < b.month ? 1 : -1));
}

/** その月の全員の平均(同じカテゴリで、その月に稼働した人の 1人ずつの月の値 の平均)。people = 人数 */
export type MonthAverage = { people: number; hours: number | null; appointments: number | null; rate: number | null; talkAvgSec: number | null; afterAvgSec: number | null; callsPerHour: number | null };

export function monthlyAverages(tracks: Track[], business: BusinessType): Map<string, MonthAverage> {
  const avg = (list: (number | null)[]) => {
    const v = list.filter((x): x is number => x !== null);
    return v.length > 0 ? v.reduce((a, b) => a + b, 0) / v.length : null;
  };
  const byMonth = new Map<string, MonthStat[]>();
  for (const t of tracks) {
    if (t.business !== business) continue;
    for (const m of monthlyStats(t.days)) byMonth.set(m.month, [...(byMonth.get(m.month) ?? []), m]);
  }
  return new Map(
    [...byMonth.entries()].map(([month, list]) => [
      month,
      {
        people: list.length,
        hours: avg(list.map((m) => m.hours)),
        appointments: avg(list.map((m) => m.appointments)),
        rate: avg(list.map((m) => m.rate)),
        talkAvgSec: avg(list.map((m) => m.calls.talkAvgSec)),
        afterAvgSec: avg(list.map((m) => m.calls.afterAvgSec)),
        callsPerHour: avg(list.map((m) => m.calls.callsPerHour)),
      },
    ]),
  );
}

export const trackKey = (opId: string, business: BusinessType) => `${opId}_${business}`;
export const savedMilestoneKey = (opId: string, business: BusinessType, m: Milestone) => `${opId}_${business}_${m}`;

const round2 = (v: number) => Math.round(v * 100) / 100;
export const rateOf = (appointments: number, hours: number): number | null => (hours > 0 ? appointments / hours : null);

/** 1日ずつの数字から、段階・区間・次の段階までを求める(days は日付順) */
export function computeMilestones(days: Pick<TrackDay, 'date' | 'appointments' | 'hours'>[]): Pick<Track, 'milestones' | 'next' | 'totalAppointments' | 'totalHours'> {
  const milestones: MilestoneResult[] = [];
  let cumH = 0;
  let cumA = 0;
  let segA = 0;
  let segH = 0;
  let i = 0;
  for (const d of days) {
    cumH += d.hours;
    cumA += d.appointments;
    segA += d.appointments;
    segH += d.hours;
    // 浮動小数の誤差で 14.999… が 15 に届かないことがないよう、小数2桁で比べる
    while (round2(cumH) >= milestoneAt(i)) {
      const segRate = rateOf(segA, segH);
      const prev = milestones[milestones.length - 1];
      milestones.push({
        milestone: milestoneAt(i),
        reachedDate: d.date,
        segAppointments: segA,
        segHours: round2(segH),
        segRate,
        diff: prev && prev.segRate !== null && segRate !== null ? segRate - prev.segRate : null,
        cumAppointments: cumA,
        cumHours: round2(cumH),
        cumRate: rateOf(cumA, cumH),
        cumDiff: prev && prev.cumRate !== null && cumH > 0 ? (rateOf(cumA, cumH) ?? 0) - prev.cumRate : null,
      });
      segA = 0;
      segH = 0;
      i++;
    }
  }
  const nextM = milestoneAt(i);
  return {
    milestones,
    next: { milestone: nextM, segAppointments: segA, segHours: round2(segH), remaining: round2(nextM - cumH) },
    totalAppointments: cumA,
    totalHours: round2(cumH),
  };
}

/** 日次集計を 人 × カテゴリ × 日 にまとめる */
export function systemDaysByTrack(summaries: Pick<DailySummary, 'date' | 'staff'>[]): Map<string, Map<DateStr, TrackDay>> {
  const out = new Map<string, Map<DateStr, TrackDay>>();
  for (const s of summaries) {
    for (const r of s.staff ?? []) {
      if (!r.op_id) continue;
      const key = trackKey(r.op_id, r.business);
      let byDate = out.get(key);
      if (!byDate) out.set(key, (byDate = new Map()));
      let day = byDate.get(s.date);
      if (!day) byDate.set(s.date, (day = { date: s.date, appointments: 0, hours: 0, byCompany: {}, source: 'system', calls: 0, talkSec: 0, afterSec: 0, afterCount: 0 }));
      const a = r.appointments ?? 0;
      const h = r.worked_hours ?? 0;
      day.appointments += a;
      day.hours += h;
      const calls = r.calls ?? 0;
      day.calls = (day.calls ?? 0) + calls;
      day.talkSec = (day.talkSec ?? 0) + (r.talk_sec ?? 0);
      day.afterSec = (day.afterSec ?? 0) + (r.after_sec ?? 0);
      // 後処理回数が無い日は通話回数で代用(dashboard/calc.ts と同じ)
      day.afterCount = (day.afterCount ?? 0) + (r.after_count && r.after_count > 0 ? r.after_count : calls);
      const c = r.company_id ?? '';
      const cell = (day.byCompany[c] ??= { appointments: 0, hours: 0 });
      cell.appointments += a;
      cell.hours += h;
    }
  }
  return out;
}

/** 稼働開始日と、新人かどうか・数え始めの日を決める */
export function resolveStart(doc: Pick<TrainingTrackDoc, 'start_date' | 'start_source'> | undefined, firstSystemDay: DateStr | null): Pick<Track, 'isNew' | 'startDate' | 'startSource' | 'countFrom'> {
  const start = doc?.start_date ?? null;
  if (start) {
    if (start >= NEW_HIRE_FROM) return { isNew: true, startDate: start, startSource: doc?.start_source ?? 'manual', countFrom: start };
    return { isNew: false, startDate: start, startSource: doc?.start_source ?? 'manual', countFrom: VETERAN_COUNT_FROM };
  }
  if (firstSystemDay && firstSystemDay >= AUTO_NEW_HIRE_FROM) return { isNew: true, startDate: firstSystemDay, startSource: 'auto', countFrom: firstSystemDay };
  return { isNew: false, startDate: null, startSource: null, countFrom: VETERAN_COUNT_FROM };
}

/**
 * 在籍中の全スタッフのトラックを作る。
 * カテゴリは その人のBB IDの架電内容と、日次集計・採用データに出てくるカテゴリの和。
 */
export function buildTracks(params: { operators: Operator[]; summaries: Pick<DailySummary, 'date' | 'staff'>[]; trackDocs: TrainingTrackDoc[]; today: DateStr }): Track[] {
  const system = systemDaysByTrack(params.summaries);
  const docs = new Map(params.trackDocs.map((d) => [d.id, d]));
  const tracks: Track[] = [];
  for (const op of params.operators) {
    if (op.status !== 'active') continue;
    const businesses = new Set<BusinessType>(op.bb_accounts.map((a) => a.business));
    for (const b of ['kyutoki', 'fuyohin'] as BusinessType[]) {
      if (system.has(trackKey(op.id, b)) || docs.has(trackKey(op.id, b))) businesses.add(b);
    }
    for (const business of (['kyutoki', 'fuyohin'] as BusinessType[]).filter((b) => businesses.has(b))) {
      const key = trackKey(op.id, business);
      const doc = docs.get(key);
      const sysDays = Array.from(system.get(key)?.values() ?? [])
        .filter((d) => d.date <= params.today)
        .sort((a, b) => (a.date < b.date ? -1 : 1));
      const firstSystemDay = sysDays.find((d) => d.hours > 0 || d.appointments > 0)?.date ?? null;
      const start = resolveStart(doc, firstSystemDay);
      const sheetDays: TrackDay[] = Object.entries(doc?.sheet_days ?? {})
        .filter(([date]) => date >= start.countFrom && date < SHEET_UNTIL)
        .map(([date, [appointments, hours]]) => ({ date, appointments, hours, byCompany: {}, source: 'sheet' as const }));
      const days = [...sheetDays, ...sysDays.filter((d) => d.date >= start.countFrom && d.date >= SHEET_UNTIL)].sort((a, b) => (a.date < b.date ? -1 : 1));
      // 最終稼働日は数え始めより前の日も見る(採用データの日も含む)
      const worked = [...Object.entries(doc?.sheet_days ?? {}).filter(([date, [a, h]]) => date < SHEET_UNTIL && (a > 0 || h > 0)).map(([date]) => date), ...sysDays.filter((d) => d.hours > 0 || d.appointments > 0).map((d) => d.date)];
      const lastWorkDate = worked.length ? worked.sort()[worked.length - 1]! : null;
      tracks.push({ key, opId: op.id, opCode: op.op_code, name: op.name, business, ...start, days, lastWorkDate, calls: callStatsOf(days), ...computeMilestones(days) });
    }
  }
  return tracks;
}

export type EffectWindow = { from: DateStr; to: DateStr; appointments: number; hours: number; rate: number | null; days: number };
export type RecordEffect = { before: EffectWindow | null; after: EffectWindow | null; afterComplete: boolean; diff: number | null };

/** 面談・研修の前後15hの成績(面談日は含めない) */
export function recordEffect(days: Pick<TrackDay, 'date' | 'appointments' | 'hours'>[], date: DateStr, hours = EFFECT_HOURS): RecordEffect {
  const collect = (list: Pick<TrackDay, 'date' | 'appointments' | 'hours'>[]) => {
    let a = 0;
    let h = 0;
    const used: DateStr[] = [];
    for (const d of list) {
      if (d.hours <= 0 && d.appointments <= 0) continue;
      a += d.appointments;
      h += d.hours;
      used.push(d.date);
      if (round2(h) >= hours) return { a, h, used, complete: true };
    }
    return { a, h, used, complete: false };
  };
  const toWindow = (r: ReturnType<typeof collect>): EffectWindow | null => {
    if (r.used.length === 0) return null;
    const sorted = [...r.used].sort();
    return { from: sorted[0]!, to: sorted[sorted.length - 1]!, appointments: r.a, hours: round2(r.h), rate: rateOf(r.a, r.h), days: r.used.length };
  };
  const sorted = [...days].sort((a, b) => (a.date < b.date ? -1 : 1));
  const b = collect(sorted.filter((d) => d.date < date).reverse());
  const a = collect(sorted.filter((d) => d.date > date));
  const before = toWindow(b);
  const after = toWindow(a);
  // 前が15hに届かない(稼働直後の面談)ときも、ある分で比べる。
  // 後が15hに届いていなくても、その時点の数字で差を出す(「12.4h時点」と表示。2026-10-09 発注者指示)。afterComplete で区別する
  const diff = before?.rate != null && after?.rate != null ? after.rate - before.rate : null;
  return { before, after, afterComplete: a.complete, diff };
}

/** 'YYYY-MM-DD' を n 日ずらす */
export function shiftDate(date: DateStr, n: number): DateStr {
  const d = new Date(`${date}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
}

/** 面談予定日の前の何日分を見るか(2026-10-15 発注者指示「面談予定日から過去30日間」) */
export const PLAN_WINDOW_DAYS = 30;

export type PlanWindowStats = {
  from: DateStr;
  to: DateStr;
  /** 稼働した日数 */
  days: number;
  hours: number;
  appointments: number;
  calls: number;
  talkSec: number;
  afterSec: number;
  /** 取得 ÷ 実稼働 */
  rate: number | null;
  /** 取得 ÷ 通話回数(%) */
  hitRate: number | null;
  /** 通話回数 ÷ 実稼働(通話の記録がある日の実稼働で割る) */
  callsPerHour: number | null;
  /** 1回あたりの通話時間・後処理時間(秒) */
  talkAvgSec: number | null;
  afterAvgSec: number | null;
  /** 期間に採用データ(通話の記録が無い日)が入っている */
  hasSheetDays: boolean;
};

/**
 * 面談予定日の前の30日間の目標(2026-10-15 発注者指示)。給湯器だけ(不用品は目標なし。D-208 と同じ)。
 * 稼働時間・取得成績/h・取得率・通話件数/h は以上が良い。通話時間・後処理時間は1件あたりの平均(秒)で、短いほど良い(2026-10-15 発注者回答)
 */
export const PLAN_TARGETS = { hours: 30, rate: 2, hitRate: 2.5, callsPerHour: 80, talkAvgSec: 22, afterAvgSec: 6 } as const;
export const planTargets = (business: BusinessType) => (business === 'kyutoki' ? PLAN_TARGETS : null);

/**
 * 面談予定日の前の30日間(予定日の30日前〜前日。予定日は含めない)の成績(2026-10-15 発注者指示)。
 * 1時間あたりは実稼働で割る(この画面の 件/h と同じ)。取得率 = 取得 ÷ 通話回数(dashboard/calc.ts と同じ)
 */
export function planWindowStats(days: TrackDay[], planDate: DateStr, n = PLAN_WINDOW_DAYS): PlanWindowStats {
  const from = shiftDate(planDate, -n);
  const to = shiftDate(planDate, -1);
  const list = days.filter((d) => d.date >= from && d.date <= to && (d.hours > 0 || d.appointments > 0));
  let hours = 0;
  let appointments = 0;
  let calls = 0;
  let talkSec = 0;
  let afterSec = 0;
  let afterCount = 0;
  let callHours = 0;
  for (const d of list) {
    hours += d.hours;
    appointments += d.appointments;
    if (d.source === 'system') {
      callHours += d.hours;
      calls += d.calls ?? 0;
      talkSec += d.talkSec ?? 0;
      afterSec += d.afterSec ?? 0;
      afterCount += d.afterCount ?? 0;
    }
  }
  return {
    from,
    to,
    days: list.length,
    hours: round2(hours),
    appointments,
    calls,
    talkSec,
    afterSec,
    rate: rateOf(appointments, hours),
    hitRate: calls > 0 ? (appointments / calls) * 100 : null,
    callsPerHour: callHours > 0 ? calls / callHours : null,
    talkAvgSec: calls > 0 ? talkSec / calls : null,
    afterAvgSec: afterCount > 0 ? afterSec / afterCount : null,
    hasSheetDays: list.some((d) => d.source === 'sheet'),
  };
}

/** その日より前(その日は含めない)の累計の実稼働。面談した時点が稼働何時間目かを出す(2026-10-14 発注者指示) */
export function hoursBefore(days: Pick<TrackDay, 'date' | 'hours'>[], date: DateStr): number {
  return round2(days.filter((d) => d.date < date).reduce((n, d) => n + d.hours, 0));
}

export type TrainingAlert = { track: Track; milestone: MilestoneResult };

export type PlanAlert = { track: Track; date: DateStr; overdue: boolean };

/**
 * 面談予定者(2026-10-15 発注者指示「上に面談予定者と面談要対応の人が出てこればOK」)。
 * 面談予定日を入れた人のうち、その日以降の面談・研修の記録がまだ無い人(記録すると消える)。予定日の早い順。
 * 予定日を過ぎても記録が無ければ overdue
 */
export function planAlerts(tracks: Track[], trackDocs: Pick<TrainingTrackDoc, 'id' | 'interview_plan_date'>[], records: Pick<TrainingRecord, 'op_id' | 'business' | 'date'>[], today: DateStr): PlanAlert[] {
  const byKey = new Map(tracks.map((t) => [t.key, t]));
  const out: PlanAlert[] = [];
  for (const d of trackDocs) {
    const date = d.interview_plan_date;
    const track = date ? byKey.get(d.id) : undefined;
    if (!date || !track) continue;
    if (records.some((r) => trackKey(r.op_id, r.business) === d.id && r.date >= date)) continue;
    out.push({ track, date, overdue: date < today });
  }
  return out.sort((a, b) => (a.date < b.date ? -1 : a.date > b.date ? 1 : a.track.name.localeCompare(b.track.name, 'ja')));
}

/** 面談の時期のアラート: 新人が 15h/30h/60h に届いたのに、その段階の面談の記録が無い */
export function trainingAlerts(tracks: Track[], records: Pick<TrainingRecord, 'op_id' | 'business' | 'milestone' | 'kind'>[]): TrainingAlert[] {
  const done = new Set(records.filter((r) => r.kind === 'interview' && r.milestone !== null).map((r) => `${trackKey(r.op_id, r.business)}_${r.milestone}`));
  const out: TrainingAlert[] = [];
  for (const t of tracks) {
    if (!t.isNew) continue;
    for (const m of t.milestones) {
      if (!(ALERT_MILESTONES as readonly number[]).includes(m.milestone)) continue;
      if (m.reachedDate < ALERT_FROM) continue;
      if (done.has(`${t.key}_${m.milestone}`)) continue;
      out.push({ track: t, milestone: m });
    }
  }
  return out.sort((a, b) => (a.milestone.reachedDate < b.milestone.reachedDate ? -1 : 1));
}

/** 保存されていない(または到達日・数字が変わった)段階の成績 */
export function unsavedMilestones(tracks: Track[], saved: SavedMilestone[]): { track: Track; milestone: MilestoneResult }[] {
  const byId = new Map(saved.map((s) => [s.id, s]));
  const out: { track: Track; milestone: MilestoneResult }[] = [];
  for (const t of tracks) {
    // 控えを保存するのは 15h〜90h だけ(120h 以降は一覧に出すだけ)
    for (const m of t.milestones.filter((x) => (MILESTONES as readonly number[]).includes(x.milestone))) {
      const s = byId.get(savedMilestoneKey(t.opId, t.business, m.milestone));
      if (s && s.reached_date === m.reachedDate && s.seg_appointments === m.segAppointments && round2(s.seg_hours) === m.segHours) continue;
      out.push({ track: t, milestone: m });
    }
  }
  return out;
}

/** 会社別の内訳(日次集計の日だけ。採用データの日は会社が分からないので company '' にまとめる) */
export function companyBreakdown(days: TrackDay[]): { companyId: string; appointments: number; hours: number }[] {
  const m = new Map<string, { appointments: number; hours: number }>();
  for (const d of days) {
    if (d.source === 'sheet') {
      const cell = m.get('') ?? { appointments: 0, hours: 0 };
      cell.appointments += d.appointments;
      cell.hours += d.hours;
      m.set('', cell);
      continue;
    }
    for (const [c, v] of Object.entries(d.byCompany)) {
      const cell = m.get(c) ?? { appointments: 0, hours: 0 };
      cell.appointments += v.appointments;
      cell.hours += v.hours;
      m.set(c, cell);
    }
  }
  return Array.from(m.entries())
    .map(([companyId, v]) => ({ companyId, appointments: v.appointments, hours: round2(v.hours) }))
    .sort((a, b) => b.hours - a.hours);
}

/** 面談・研修1件と、その前後15hの成績(一覧の「面談の効果」と、まとめて比べる表示に使う。2026-10-09) */
export type EffectRow = { record: TrainingRecord; track: Track | null; effect: RecordEffect };

/** 記録ごとの前後15hの成績(新しい日付順)。指導は入れない(2026-10-20 発注者回答) */
export function effectRows(tracks: Track[], records: TrainingRecord[]): EffectRow[] {
  const byKey = new Map(tracks.map((t) => [t.key, t]));
  const empty: RecordEffect = { before: null, after: null, afterComplete: false, diff: null };
  return records
    .filter((record) => countsForEffect(record.kind))
    .map((record) => {
      const track = byKey.get(trackKey(record.op_id, record.business)) ?? null;
      return { record, track, effect: track ? recordEffect(track.days, record.date) : empty };
    })
    .sort((a, b) => (a.record.date < b.record.date ? 1 : a.record.date > b.record.date ? -1 : 0));
}

/** 人 × カテゴリごとの、いちばん新しい記録 */
export function latestEffectByTrack(rows: EffectRow[]): Map<string, EffectRow> {
  const out = new Map<string, EffectRow>();
  for (const r of rows) {
    const k = trackKey(r.record.op_id, r.record.business);
    const cur = out.get(k);
    if (!cur || r.record.date > cur.record.date) out.set(k, r);
  }
  return out;
}

export type EffectGroupBy = 'trainer' | 'milestone' | 'kind' | 'business';

export type EffectGroup = {
  key: string;
  /** まとめの名前(担当者名・段階・種類。カテゴリ別のときはカテゴリ) */
  label: string;
  /** カテゴリ(担当者別・段階別・種類別は給湯器/不用品に分ける。2026-10-09 発注者指示) */
  business: BusinessType;
  /** 記録の件数 */
  count: number;
  /** 差が出た件数(平均・伸びた人数はこの件数で数える。後が15h未達の途中の数字も入る) */
  measured: number;
  /** measured のうち、後が15hに届いていない(途中の数字の)件数 */
  partial: number;
  /** 差がプラスの件数 */
  improved: number;
  /** 差の平均(1件ずつの差の単純平均) */
  avgDiff: number | null;
  /** 前・後の15hの合計の 取得÷時間 */
  beforeRate: number | null;
  afterRate: number | null;
};

/**
 * 記録をまとめて比べる(担当者別・段階別・種類別・カテゴリ別)。後が15hに届いていない記録も、その時点の数字で平均に入れ、件数を partial に数える(2026-10-09)。
 * 担当者別・段階別・種類別は、さらに給湯器/不用品に分ける(1時間あたりの水準がカテゴリで大きく違うため。2026-10-09)
 */
export function summarizeEffects(rows: EffectRow[], by: EffectGroupBy): EffectGroup[] {
  const labelOf = (r: EffectRow): string =>
    by === 'trainer' ? r.record.trainer.trim() || '(未入力)' : by === 'milestone' ? (r.record.milestone ? `${r.record.milestone}h` : '段階と関係ない') : by === 'kind' ? RECORD_KIND_LABELS[r.record.kind] : r.record.business;
  const groups = new Map<string, { label: string; business: BusinessType; count: number; diffs: number[]; partial: number; ba: number; bh: number; aa: number; ah: number }>();
  for (const r of rows) {
    const label = labelOf(r);
    const k = `${label}_${r.record.business}`;
    const g = groups.get(k) ?? { label, business: r.record.business, count: 0, diffs: [], partial: 0, ba: 0, bh: 0, aa: 0, ah: 0 };
    g.count++;
    if (r.effect.diff !== null && r.effect.before && r.effect.after) {
      g.diffs.push(r.effect.diff);
      if (!r.effect.afterComplete) g.partial++;
      g.ba += r.effect.before.appointments;
      g.bh += r.effect.before.hours;
      g.aa += r.effect.after.appointments;
      g.ah += r.effect.after.hours;
    }
    groups.set(k, g);
  }
  return Array.from(groups.entries())
    .map(([key, g]) => ({
      key,
      label: g.label,
      business: g.business,
      count: g.count,
      measured: g.diffs.length,
      partial: g.partial,
      improved: g.diffs.filter((d) => Math.round(d * 100) > 0).length,
      avgDiff: g.diffs.length ? g.diffs.reduce((s, d) => s + d, 0) / g.diffs.length : null,
      beforeRate: rateOf(g.ba, g.bh),
      afterRate: rateOf(g.aa, g.ah),
    }))
    // 名前順(15h・30h… は数字の順)、同じ名前の中は 給湯器 → 不用品
    .sort((a, b) => {
      const byBusiness = a.business === b.business ? 0 : a.business === 'kyutoki' ? -1 : 1;
      return by === 'business' ? byBusiness : a.label.localeCompare(b.label, 'ja', { numeric: true }) || byBusiness;
    });
}

// ---- 一覧の絞り込み(2026-10-14 発注者指示) ----

/** 届いた段階のうちいちばん上(まだ 15h に届いていなければ 0) */
export function reachedStage(t: Pick<Track, 'milestones'>): number {
  return t.milestones.length ? t.milestones[t.milestones.length - 1]!.milestone : 0;
}

export type GoalKey = '30ok' | '30ng' | '60ok' | '60ng';
/** 目標(30h・60h の累計の件/h)の達成・未達。届いていない段階・目標の無いカテゴリは出さない */
export function goalKeys(t: Pick<Track, 'business' | 'milestones'>): GoalKey[] {
  const out: GoalKey[] = [];
  for (const m of [30, 60] as const) {
    const target = rateTarget(t.business, m);
    const r = t.milestones.find((x) => x.milestone === m);
    if (target === null || !r || r.cumRate === null) continue;
    out.push(`${m}${r.cumRate >= target ? 'ok' : 'ng'}` as GoalKey);
  }
  return out;
}

export type LastWorkKey = 'within7' | 'within30' | 'over30' | 'none';
/** 最終稼働日が今日から何日前か(7日以内 / 8〜30日 / 31日以上 / 稼働なし) */
export function lastWorkKey(lastWorkDate: DateStr | null, today: DateStr): LastWorkKey {
  if (!lastWorkDate) return 'none';
  const days = Math.round((Date.parse(`${today}T00:00:00Z`) - Date.parse(`${lastWorkDate}T00:00:00Z`)) / 86_400_000);
  return days <= 7 ? 'within7' : days <= 30 ? 'within30' : 'over30';
}
